// Lib
import { captureException } from '@sentry/browser';
import { call, put, select, takeEvery } from 'redux-saga/effects';

// Api
import client from '@api';
import { ErrorCategory, handleError } from '@services/errorService';

// Actions
import {
  GetUserAnalytics,
  SetUserAnalytics,
  UpdatedMarbleUser,
  UserAdd,
  UserError,
  UserGetAccountById,
} from '@actions/user';
import { SetLoading } from '@actions/loading';

// Graph
import { getAccountByNameQuery } from '@graphql/queries/account';

// Queries
import {
  getAccountEngagement,
  getAccountScoreQuery,
  getAllAnalyticsQuery,
  getProfileLinkClicks,
  getProfileStatsQuery,
  getProfileViews,
} from '../../graphql/queries/analytics';

// Utils
import getLatestDates from '@utils/Date';

interface BackgroundSettings {
  colorMode?: string;
  [key: string]: unknown;
}

interface UpdatedMarbleUserPayload {
  backgroundSettings?: BackgroundSettings;
}

interface UserGetAccountByIdPayload {
  username?: string;
  domain?: string;
}

interface GetUserAnalyticsPayload {
  period?: string;
  typeQuery?:
    | 'profileViews'
    | 'engagement'
    | 'profileLinkClicks'
    | 'profileScore'
    | 'stats';
}

interface Auth {
  source: string;
}

interface User {
  auths: Auth[];
  [key: string]: unknown;
}

interface ProfileViewByDate {
  date: string;
  total: number;
}

interface AccountAnalytics {
  profileViews?: unknown;
  engagement?: unknown;
  profileLinkClicks?: unknown;
  profileViewsByDates?: ProfileViewByDate[];
}

interface PlatformStat {
  platform: string;
  [key: string]: unknown;
}

interface AccountScore {
  [key: string]: unknown;
}

interface SagaAction<T> {
  type: string;
  payload: T;
}

function* updatedMarbleUserSaga({
  payload,
}: SagaAction<UpdatedMarbleUserPayload>): Generator {
  try {
    if (payload && payload.backgroundSettings) {
      (yield select((state) => ({
        ...state.User,
        backgroundSettings: payload.backgroundSettings,
      }))) as unknown;
      // Scene3D.action(UPDATE_USER, User);
    }
  } catch (error) {
    captureException(error);
  }
}

function* userGetAccountByIdSaga({
  payload,
}: SagaAction<UserGetAccountByIdPayload>): Generator {
  try {
    if (!payload) return;

    yield put(SetLoading(true));
    const {
      data: { getAccount },
    } = (yield call([client(), 'query'], {
      query: getAccountByNameQuery,
      variables: { input: { ...payload } },
      fetchPolicy: 'no-cache',
    })) as { data: { getAccount: Record<string, unknown> } };

    yield put(UserAdd({ ...getAccount }));
    yield put(SetLoading(false));
  } catch (error) {
    yield put(UserError(error));
    yield put(SetLoading(false));
  }
}

function* getUserAnalyticsSaga({
  payload,
}: SagaAction<GetUserAnalyticsPayload>): Generator {
  try {
    yield put(SetUserAnalytics({ loading: true }));

    const user = (yield select((state: { User: User }) => state.User)) as User;
    const userApps: string[] = user.auths.map((auth: Auth) =>
      auth.source.toLowerCase(),
    );

    let analyticsData: Record<string, unknown> = {};

    let daysNumber = +payload?.period.match(/\d+/)[0];
    daysNumber = daysNumber ? daysNumber : 7;
    daysNumber = daysNumber === 3 ? 90 : daysNumber;

    if (payload?.typeQuery === 'profileViews') {
      const {
        data: {
          getAccountAnalytics: { profileViews },
        },
      } = (yield call([client(), 'query'], {
        query: getProfileViews,
        variables: { input: { period: daysNumber } },
      })) as { data: { getAccountAnalytics: { profileViews: unknown } } };
      analyticsData = { profileViews };
    }

    if (payload?.typeQuery === 'engagement') {
      const {
        data: {
          getAccountAnalytics: { engagement },
        },
      } = (yield call([client(), 'query'], {
        query: getAccountEngagement,
        variables: { input: { period: daysNumber } },
      })) as { data: { getAccountAnalytics: { engagement: unknown } } };
      analyticsData = { engagement };
    }

    if (payload?.typeQuery === 'profileLinkClicks') {
      const {
        data: {
          getAccountAnalytics: { profileLinkClicks },
        },
      } = (yield call([client(), 'query'], {
        query: getProfileLinkClicks,
        variables: { input: { period: daysNumber } },
      })) as { data: { getAccountAnalytics: { profileLinkClicks: unknown } } };
      analyticsData = { profileLinkClicks };
    }

    if (payload?.typeQuery === 'profileScore') {
      const {
        data: { getAccountScore },
      } = (yield call([client(), 'query'], {
        query: getAccountScoreQuery,
        variables: {
          input: {
            isCompleted: false,
          },
        },
      })) as { data: { getAccountScore: AccountScore[] } };
      analyticsData = { profileScore: getAccountScore[0] };
    }

    if (payload?.typeQuery === 'stats') {
      const {
        data: { getAccountPlatformStats, getAccountAnalytics },
      } = (yield call([client(), 'query'], {
        query: getProfileStatsQuery,
        variables: { input: { period: daysNumber } },
      })) as {
        data: {
          getAccountPlatformStats: PlatformStat[];
          getAccountAnalytics: AccountAnalytics;
        };
      };
      analyticsData = {
        socialStats: getAccountPlatformStats.filter(
          ({ platform }: PlatformStat) =>
            userApps.includes(platform.toLowerCase()),
        ),
        graphicViews: getLatestDates(daysNumber).map((item: string) => {
          const searchDate = getAccountAnalytics?.profileViewsByDates?.find(
            ({ date }: ProfileViewByDate) =>
              new Date(date).toDateString() === item,
          );
          if (searchDate) {
            return { date: item, total: searchDate?.total };
          }
          return { date: item, total: 0 };
        }),
        profileVisitors: getAccountAnalytics?.profileViewsByDates?.reduce(
          (acc: number, { total }: ProfileViewByDate) => acc + total,
          0,
        ),
      };
    }

    if (!payload?.typeQuery) {
      const {
        data: { getAccountAnalytics, getAccountPlatformStats, getAccountScore },
      } = (yield call([client(), 'query'], {
        query: getAllAnalyticsQuery,
        variables: {
          input: { period: daysNumber },
        },
      })) as {
        data: {
          getAccountAnalytics: AccountAnalytics;
          getAccountPlatformStats: PlatformStat[];
          getAccountScore: AccountScore[];
        };
      };

      analyticsData = {
        graphicViews: getLatestDates(daysNumber).map((item: string) => {
          const searchDate = getAccountAnalytics?.profileViewsByDates?.find(
            ({ date }: ProfileViewByDate) =>
              new Date(date).toDateString() === item,
          );
          if (searchDate) {
            return { date: item, total: searchDate?.total };
          }
          return { date: item, total: 0 };
        }),
        profileVisitors: getAccountAnalytics?.profileViewsByDates?.reduce(
          (acc: number, { total }: ProfileViewByDate) => acc + total,
          0,
        ),
        profileScore: getAccountScore[0],
        socialStats: getAccountPlatformStats.filter(
          ({ platform }: PlatformStat) =>
            userApps.includes(platform.toLowerCase()),
        ),
        ...getAccountAnalytics,
      };
    }

    yield put(
      SetUserAnalytics({
        loading: false,
        analyticsData,
      }),
    );
  } catch (error) {
    yield put(SetUserAnalytics({ loading: false }));
    handleError(error, { category: ErrorCategory.GRAPHQL, silent: true });
  }
}

function* profileSaga(): Generator {
  yield takeEvery(UpdatedMarbleUser, updatedMarbleUserSaga);
  yield takeEvery(UserGetAccountById, userGetAccountByIdSaga);
  yield takeEvery(GetUserAnalytics, getUserAnalyticsSaga);
}

export default profileSaga;
